// components/LocationsList.jsx
import { Box, List, ListItem, ListItemButton, ListItemText, Typography, Collapse, Tooltip } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faCircleO } from '@fortawesome/pro-regular-svg-icons';
import { faCube } from '@fortawesome/pro-regular-svg-icons';
import { faCircleSmall } from '@fortawesome/pro-solid-svg-icons';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import { motion } from 'framer-motion';
import React, { useState, useMemo } from 'react';
import SearchBox from './SearchBox';
import { useCameraStatus } from './context/CameraStatusContext';
const LocationsList = ({ data, expandedSite, expandedAsset, darkMode, onSiteClick, onShowAllCameras, onFetchCameras, setExpandedAsset }) => {
	const [search, setSearch] = useState('');
	const { cameraStatusMap } = useCameraStatus();

	const hoverColor = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(63, 81, 181, 0.08)';
	const filteredData = useMemo(() => {
		if (!search.trim()) return data;

		const lowerSearch = search.toLowerCase();
		const result = {};

		for (const [siteId, site] of Object.entries(data)) {
			const matchedAssets = Object.entries(site.assets || {}).filter(([, asset]) => asset.asset_name.toLowerCase().includes(lowerSearch));

			if (site.site_name.toLowerCase().includes(lowerSearch) || matchedAssets.length > 0) {
				result[siteId] = {
					...site,
					assets: Object.fromEntries(matchedAssets),
				};
			}
		}

		return result;
	}, [search, data]);

	return (
		<Box
			sx={{
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}
		>
			<div
				style={{
					padding: '8px 16px',
					flexShrink: 0,
				}}
			>
				<SearchBox value={search} onChange={(e) => setSearch(e.target.value)} onSearch={() => { }} />
			</div>
			{/* Sites List */}
			<Box sx={{ display: 'flex', flexDirection: 'column', height: '90%', justifyContent: 'space-between' }}>
				<List
					sx={{
						'& .MuiListItemButton-root:hover': { bgcolor: hoverColor },
						flex: 1,
						overflowY: 'auto',
					}}
				>
					{filteredData && Object.entries(filteredData).map(([siteId, site]) => (
						<motion.div key={siteId} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
							<ListItem disablePadding sx={{ p: 0.5 }}>
								<ListItemButton
									onClick={() => onSiteClick(siteId, site)}
									sx={{
										color: expandedSite === siteId ? '#1f60c0' : '',
									}}
								>
									<Box display='flex' alignItems='center' gap={1}>
										{expandedSite === siteId ? <RemoveOutlinedIcon sx={{ color: '#8F8F8F' }} fontSize='1.4em' /> : <AddOutlinedIcon sx={{ color: '#8F8F8F' }} fontSize='1.5em' />}
										<FontAwesomeIcon icon={faCircleO} style={{ color: '#1f60c0', fontSize: '13px' }} />
										{/* </svg> */}
										<ListItemText
											primary={site.site_name}
											primaryTypographyProps={{
												sx: {
													display: '-webkit-box',
													fontWeight: 600,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													WebkitLineClamp: 1,
													WebkitBoxOrient: 'vertical',
													width: '100%',
													color: '#585858',
													fontSize: '0.9rem'
												},
											}}
										/>
										<Typography variant='body2' color='rgb(79, 70, 229)' sx={{ ml: 'auto', pr: 2, fontWeight: 600 }}>
											({site?.assets ? Object.keys(site?.assets).length : 0})
										</Typography>
									</Box>
								</ListItemButton>
							</ListItem>

							{/* Assets List */}
							<Collapse in={expandedSite === siteId} timeout='auto' unmountOnExit>
								<List component='div' disablePadding sx={{ pl: 2 }}>
									{Object.entries(site.assets || {}).map(([assetId, asset]) => {
										const isCameraAvailable = (asset.cameras || []).some((cam) => cameraStatusMap[cam.camera_id]);
										return (
											<ListItem key={assetId} disablePadding >
												<ListItemButton sx={{ pl: 2, ml: 2 }}>
													<Box
														gap={1}
														sx={{
															backgroundColor: expandedAsset === assetId ? '#f0f4ff' : 'inherit',
															borderRadius: expandedAsset === assetId ? '6px' : '',
															padding: '0px 6px',
															color: '#585858',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'space-between',
															width: '100%',
														}}
													>
														<FontAwesomeIcon icon={faCube} style={{ color: '#1f60c0', fontSize: '14px' }} />
														<Tooltip title={asset.asset_name}>
															<ListItemText
																sx={{
																	display: '-webkit-box',
																	overflow: 'hidden',
																	textOverflow: 'ellipsis',
																	WebkitLineClamp: 1,
																	WebkitBoxOrient: 'vertical',
																	width: '100%',
																}}
																primary={asset.asset_name}
																primaryTypographyProps={{
																	sx: {
																		fontWeight: 600,
																		color: expandedAsset === assetId ? '#1f60c0' : 'inherit',
																		fontSize: '0.8rem'
																	},
																}}
																onClick={() => {
																	setExpandedAsset(assetId);
																	onFetchCameras(siteId, assetId);
																}}
															/>
														</Tooltip>
														<Box sx={{ pr: 1 }}>
															<FontAwesomeIcon
																icon={faCircleSmall}
																style={{
																	color: isCameraAvailable ? 'rgb(40, 167, 69)' : 'rgb(108, 117, 125)',
																	fontSize: '12px',
																}}
															/>
														</Box>
													</Box>
												</ListItemButton>
											</ListItem>
										);
									})}
								</List>
							</Collapse>
						</motion.div>
					))}
				</List>

				<Box
					sx={{
						borderTop: '1px solid #e5e7eb',
						p: 1,
						m: 2,
					}}
				>
					{/* Title */}
					<Typography
						variant="subtitle2"
						sx={{
							color: '#374151',
							fontWeight: 600,
							mb: 1,
							fontSize: '0.75rem'
						}}
					>
						Analytics Legend:
					</Typography>

					{/* Legend List */}
					<List disablePadding dense gap={1}>
						<ListItem sx={{ py: 0.1, gap: '0.25rem' }}>
							<Box
								sx={{
									width: '8px',
									height: '8px',
									borderRadius: '50%',
									backgroundColor: 'green',

								}}
							/>
							<ListItemText
								primary="Pre-work Analytics"
								primaryTypographyProps={{
									variant: 'body2',
									sx: { color: '#374151', fontSize: '0.7rem' },
								}}
							/>
						</ListItem>

						<ListItem sx={{ py: 0.1, gap: '0.25rem' }}>
							<Box
								sx={{
									width: '8px',
									height: '8px',
									borderRadius: '50%',
									backgroundColor: 'blue',

								}}
							/>
							<ListItemText
								primary="Post-work Analytics"
								primaryTypographyProps={{
									variant: 'body2',
									sx: { color: '#374151', fontSize: '0.7rem' },
								}}
							/>
						</ListItem>

						<ListItem sx={{ py: 0.1, gap: '0.25rem' }} >
							<Box
								sx={{
									width: '8px',
									height: '8px',
									borderRadius: '50%',
									backgroundColor: 'grey',

								}}
							/>
							<ListItemText
								primary="Not Available"
								primaryTypographyProps={{
									variant: 'body2',
									sx: { color: '#374151', fontSize: '0.7rem' },
								}}
							/>
						</ListItem>
					</List>


				</Box>
				{/* Show All Cameras Button */}
				<Box
					sx={{
						borderTop: '1px solid #e0e0e0',
						mt: 'auto',
						position: 'sticky',
						bottom: 0,
						backgroundColor: darkMode ? 'rgba(26, 26, 46, 0.9)' : 'rgba(255, 255, 255, 0.9)',
						backdropFilter: 'blur(8px)',
					}}
				>
					<ListItem disablePadding>
						<ListItemButton
							onClick={onShowAllCameras}
							sx={{
								bgcolor: hoverColor,
								py: 1.5,
							}}
						>
							<Box display='flex' alignItems='center' gap={1}>
								<FontAwesomeIcon icon={faCamera} style={{ color: '#1f60c0', fontSize: '14px' }} />
								<ListItemText
									primary='Show All Cameras'
									primaryTypographyProps={{
										sx: {
											fontWeight: 600,
											color: '#585858',
										},
									}}
								/>
							</Box>
						</ListItemButton>
					</ListItem>
				</Box>
			</Box>
		</Box>
	);
};

export default LocationsList;
